import { useEffect, useRef } from 'react'
import { useToast } from '~/shared/lib'

const CHECK_INTERVAL_MS = 60_000
const REQUEST_TIMEOUT_MS = 10_000
const CURRENT_APP_VERSION = import.meta.env.VITE_APP_VERSION

interface VersionResponse {
  version?: string
}

const VersionUpdateNotifier = () => {
  const toast = useToast()
  const isNotifiedRef = useRef(false)
  const isCheckingRef = useRef(false)

  useEffect(() => {
    const checkVersion = async () => {
      if (isCheckingRef.current) return

      isCheckingRef.current = true
      const controller = new AbortController()
      const timeoutId = window.setTimeout(
        () => controller.abort(),
        REQUEST_TIMEOUT_MS,
      )

      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          signal: controller.signal,
        })

        if (!response.ok) return

        const data = (await response.json()) as VersionResponse
        const latestVersion = data.version?.trim()

        if (
          !latestVersion ||
          latestVersion === CURRENT_APP_VERSION ||
          isNotifiedRef.current
        ) {
          return
        }

        isNotifiedRef.current = true
        toast.open(
          <div className="flex flex-col gap-2">
            <span>
              Доступна новая версия приложения. Обновите страницу, чтобы
              применить изменения.
            </span>

            <button
              type="button"
              className="w-fit rounded-md bg-white px-3 py-1 text-sm font-medium text-yellow-700 hover:bg-yellow-50"
              onClick={() => window.location.reload()}
            >
              Обновить страницу
            </button>
          </div>,
          'warning',
        )
      } catch {
        // Version checks must not interrupt the main app flow.
      } finally {
        window.clearTimeout(timeoutId)
        isCheckingRef.current = false
      }
    }

    checkVersion()
    const timerId = window.setInterval(checkVersion, CHECK_INTERVAL_MS)

    return () => window.clearInterval(timerId)
  }, [toast])

  return null
}

export default VersionUpdateNotifier
